import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { BellRing, CalendarClock, Clock, Plus, Trash2, X } from "lucide-react";
import {
  useNews,
  useAddNewsEvent,
  useDeleteNewsEvent,
  useAddNewsReminder,
  useDeleteNewsReminder,
} from "../hooks/useDashboardData";
import { CardSkeleton } from "../components/Skeleton";
import type { NewsEvent, NewsReminder } from "../types";

// Every time on this page is Malaysia time, whatever timezone the viewing device is set to.
const MYT = "Asia/Kuala_Lumpur";
const timeFormat = new Intl.DateTimeFormat("en-US", { timeZone: MYT, hour: "numeric", minute: "2-digit" });
const dayFormat = new Intl.DateTimeFormat("en-GB", { timeZone: MYT, weekday: "long", day: "numeric", month: "long" });
const dayKeyFormat = new Intl.DateTimeFormat("en-CA", { timeZone: MYT, year: "numeric", month: "2-digit", day: "2-digit" });
const clockFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: MYT,
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const LEAD_OPTIONS = [
  { minutes: 0, label: "At release" },
  { minutes: 5, label: "5 min before" },
  { minutes: 15, label: "15 min before" },
  { minutes: 30, label: "30 min before" },
  { minutes: 60, label: "1 hour before" },
  { minutes: 120, label: "2 hours before" },
  { minutes: 1440, label: "1 day before" },
];

const KEY_EVENTS = [
  { tag: "FOMC", pattern: /FOMC|Federal Funds Rate/i },
  { tag: "NFP", pattern: /Non-Farm/i },
  { tag: "CPI", pattern: /\bCPI\b/i },
];

// An event drops off the default view an hour after release, so a number that just came
// out stays on screen while it's still moving the market.
const PAST_GRACE_MS = 60 * 60 * 1000;

function keyTag(title: string): string | null {
  return KEY_EVENTS.find((k) => k.pattern.test(title))?.tag ?? null;
}

function eventDate(event: NewsEvent): Date {
  return new Date(event.event_time.replace(" ", "T") + "Z");
}

function leadLabel(minutes: number): string {
  return LEAD_OPTIONS.find((o) => o.minutes === minutes)?.label ?? `${minutes} min before`;
}

function relative(msUntil: number): string {
  const minutes = Math.round(Math.abs(msUntil) / 60_000);
  if (minutes < 1) return "now";
  const text =
    minutes < 60
      ? `${minutes}m`
      : minutes < 1440
        ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`
        : `${Math.floor(minutes / 1440)}d ${Math.floor((minutes % 1440) / 60)}h`;
  return msUntil > 0 ? `in ${text}` : `${text} ago`;
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function News() {
  const { data, isLoading } = useNews();
  const addReminder = useAddNewsReminder();
  const deleteReminder = useDeleteNewsReminder();
  const deleteEvent = useDeleteNewsEvent();
  const now = useNow(30_000);

  const [keyOnly, setKeyOnly] = useState(false);
  const [currency, setCurrency] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [adding, setAdding] = useState(false);

  const events = useMemo(() => data?.events ?? [], [data]);
  const currencies = useMemo(() => [...new Set(events.map((e) => e.country))].sort(), [events]);
  const remindersByEvent = useMemo(() => {
    const map = new Map<number, NewsReminder[]>();
    for (const r of data?.reminders ?? []) map.set(r.event_id, [...(map.get(r.event_id) ?? []), r]);
    return map;
  }, [data]);

  const visible = events.filter(
    (e) =>
      (!keyOnly || keyTag(e.title)) &&
      (!currency || e.country === currency) &&
      (showPast || eventDate(e).getTime() > now - PAST_GRACE_MS),
  );
  const nextUp = visible.find((e) => eventDate(e).getTime() > now);

  const days: { key: string; label: string; events: NewsEvent[] }[] = [];
  for (const event of visible) {
    const key = dayKeyFormat.format(eventDate(event));
    if (days.at(-1)?.key !== key) days.push({ key, label: dayFormat.format(eventDate(event)), events: [] });
    days.at(-1)!.events.push(event);
  }
  const todayKey = dayKeyFormat.format(now);
  const tomorrowKey = dayKeyFormat.format(now + 24 * 60 * 60 * 1000);

  const actionError = addReminder.error ?? deleteReminder.error ?? deleteEvent.error;

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 text-sm text-slate-300">
            <Clock className="h-4 w-4 text-emerald-400" />
            All times are Malaysia time (GMT+8) &middot; now {clockFormat.format(now)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            High-impact events from the Forex Factory calendar, updated every morning at 7:10 AM.
            It only publishes one week at a time, so add anything further out yourself.
          </p>
        </div>
        <button
          onClick={() => setAdding((v) => !v)}
          className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600 hover:bg-slate-800"
        >
          <Plus className="h-3.5 w-3.5" />
          Add event
        </button>
      </div>

      {adding && <AddEventForm onDone={() => setAdding(false)} />}

      {!data.hasPushDevice && (
        <div className="rounded-lg border border-amber-900/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-300">
          Reminders arrive as push notifications, and none of your devices has notifications turned on.
          Turn them on in{" "}
          <Link to="/settings" className="font-medium underline underline-offset-2 hover:text-amber-200">
            Settings
          </Link>{" "}
          on each device you want reminders on.
        </div>
      )}

      {actionError && (
        <div className="rounded-lg border border-rose-900 bg-rose-950/50 px-3 py-2 text-sm text-rose-400">
          {actionError.message}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <FilterChip active={keyOnly} onClick={() => setKeyOnly((v) => !v)}>
          FOMC &middot; NFP &middot; CPI only
        </FilterChip>
        <span className="mx-1 h-5 w-px bg-slate-800" />
        <FilterChip active={currency === null} onClick={() => setCurrency(null)}>
          All
        </FilterChip>
        {currencies.map((c) => (
          <FilterChip key={c} active={currency === c} onClick={() => setCurrency(currency === c ? null : c)}>
            {c}
          </FilterChip>
        ))}
        <label className="ml-auto flex items-center gap-2 text-sm text-slate-400">
          <input
            type="checkbox"
            checked={showPast}
            onChange={(e) => setShowPast(e.target.checked)}
            className="h-4 w-4 rounded border-slate-600 bg-slate-900 accent-emerald-500"
          />
          Show past week
        </label>
      </div>

      {nextUp && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-900/60 bg-emerald-950/20 p-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-emerald-400">Next up</p>
            <p className="mt-1 text-base font-semibold text-slate-100">
              {nextUp.country} {nextUp.title}
            </p>
            <p className="text-sm text-slate-400">
              {dayFormat.format(eventDate(nextUp))}, {timeFormat.format(eventDate(nextUp))}
            </p>
          </div>
          <div className="text-2xl font-semibold tabular-nums text-emerald-400">
            {relative(eventDate(nextUp).getTime() - now)}
          </div>
        </div>
      )}

      {days.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-5 py-10 text-center text-sm text-slate-500">
          {events.length === 0
            ? "No news synced yet. The list fills in at the next morning update (7:10 AM)."
            : "No events match these filters."}
        </div>
      ) : (
        days.map((day) => (
          <section key={day.key} className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/60">
            <h2 className="flex items-center gap-2 border-b border-slate-800 px-4 py-3 text-sm font-semibold text-slate-100">
              <CalendarClock className="h-4 w-4 text-slate-500" />
              {day.label}
              {day.key === todayKey && <span className="text-xs font-medium text-emerald-400">Today</span>}
              {day.key === tomorrowKey && <span className="text-xs font-medium text-slate-400">Tomorrow</span>}
            </h2>
            <ul className="divide-y divide-slate-800/70">
              {day.events.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  reminders={remindersByEvent.get(event.id) ?? []}
                  now={now}
                  onAddReminder={(minutesBefore) => addReminder.mutate({ eventId: event.id, minutesBefore })}
                  onDeleteReminder={(id) => deleteReminder.mutate(id)}
                  onDelete={() => {
                    if (window.confirm(`Delete "${event.country} ${event.title}" and its reminders?`)) {
                      deleteEvent.mutate(event.id);
                    }
                  }}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-400"
          : "border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

function EventRow({
  event,
  reminders,
  now,
  onAddReminder,
  onDeleteReminder,
  onDelete,
}: {
  event: NewsEvent;
  reminders: NewsReminder[];
  now: number;
  onAddReminder: (minutesBefore: number) => void;
  onDeleteReminder: (id: number) => void;
  onDelete: () => void;
}) {
  const at = eventDate(event).getTime();
  const isPast = at <= now;
  const tag = keyTag(event.title);
  const taken = new Set(reminders.map((r) => r.minutes_before));
  const leadChoices = LEAD_OPTIONS.filter((o) => !taken.has(o.minutes) && at - o.minutes * 60_000 > now);

  return (
    <li className={`flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start ${isPast ? "opacity-60" : ""}`}>
      <div className="flex items-baseline gap-3 sm:w-28 sm:shrink-0 sm:flex-col sm:gap-0.5">
        <span className="text-base font-semibold tabular-nums text-slate-100">{timeFormat.format(at)}</span>
        <span className={`text-xs tabular-nums ${isPast ? "text-slate-500" : "text-emerald-400"}`}>
          {relative(at - now)}
        </span>
      </div>

      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded bg-slate-800 px-1.5 py-0.5 text-xs font-semibold text-slate-200">{event.country}</span>
          <span className="text-sm font-medium text-slate-100">{event.title}</span>
          {tag && (
            <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-xs font-semibold text-rose-400">{tag}</span>
          )}
          {event.source === "manual" && <span className="text-xs text-slate-500">added by you</span>}
        </div>
        {(event.forecast || event.previous) && (
          <p className="text-xs text-slate-500">
            {event.forecast && <>Forecast <span className="text-slate-300">{event.forecast}</span></>}
            {event.forecast && event.previous && " · "}
            {event.previous && <>Previous <span className="text-slate-300">{event.previous}</span></>}
          </p>
        )}

        {(reminders.length > 0 || leadChoices.length > 0) && (
          <div className="flex flex-wrap items-center gap-2">
            {reminders.map((r) => (
              <span
                key={r.id}
                className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
                  r.sent_at ? "border-slate-800 text-slate-500" : "border-emerald-800/60 text-emerald-300"
                }`}
              >
                <BellRing className="h-3 w-3" />
                {leadLabel(r.minutes_before)}
                {r.sent_at && " · sent"}
                <button
                  onClick={() => onDeleteReminder(r.id)}
                  className="rounded-full p-0.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
                  aria-label={`Remove the ${leadLabel(r.minutes_before)} reminder`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {leadChoices.length > 0 && (
              <select
                value=""
                onChange={(e) => e.target.value !== "" && onAddReminder(Number(e.target.value))}
                className="rounded-full border border-slate-700 bg-slate-950 px-2 py-0.5 text-xs text-slate-300 outline-none hover:border-slate-600 focus:ring-2 focus:ring-emerald-500/50"
                aria-label="Add a reminder"
              >
                <option value="">+ Remind me</option>
                {leadChoices.map((o) => (
                  <option key={o.minutes} value={o.minutes}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {event.source === "manual" && (
        <button
          onClick={onDelete}
          className="self-start rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-rose-400"
          aria-label="Delete this event"
          title="Delete this event"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}

function AddEventForm({ onDone }: { onDone: () => void }) {
  const addEvent = useAddNewsEvent();
  const [title, setTitle] = useState("");
  const [country, setCountry] = useState("USD");
  const [when, setWhen] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // datetime-local has no timezone - the value is the Malaysia wall-clock time typed in.
    try {
      await addEvent.mutateAsync({ title, country, eventTime: new Date(`${when}:00+08:00`).toISOString() });
      onDone();
    } catch {
      // shown below via addEvent.error
    }
  }

  const inputClass =
    "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-emerald-500/50 placeholder:text-slate-600 focus:ring-2";

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_6rem_14rem]">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-300">Event</label>
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Non-Farm Employment Change"
            className={inputClass}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-300">Currency</label>
          <input required value={country} onChange={(e) => setCountry(e.target.value)} maxLength={4} className={inputClass} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-300">Malaysia date &amp; time</label>
          <input
            required
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className={`${inputClass} [color-scheme:dark]`}
          />
        </div>
      </div>
      {addEvent.error && <p className="text-sm text-rose-400">{addEvent.error.message}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={addEvent.isPending}
          className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {addEvent.isPending ? "Adding..." : "Add"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
